// Realistic aerial-view textures for the 2D Plan Designer.
// Each texture is a seamless 512px tile representing `tileM` metres on the ground.
const SIZE = 512;
const cache = new Map();

const TILE_M = {
  turf: 3, artificial: 1.5, sandstone: 2.4, porcelain: 1.8, concslab: 1.8, concrete: 2, block: 1.2, resin: 1, tarmac: 1.2,
  gravel: 0.8, decking: 1.4, brick: 0.9, concblock: 1.8, sleeper: 1.2, render: 2, timber: 1.2, planting: 2, hedge: 1.2,
  water: 2, glass: 1.5, soil: 2, cobble: 0.6, kerb: 1.8, slate: 1.2,
  ashlar: 1.8, setts: 0.6, crazy: 1.6, drystone: 1.2, bark: 0.8, pebbles: 1, grasscrete: 1.2, slatted: 1, corten: 1,
  rooftile: 1.5, woodplank: 2.4, veg: 2, pool: 2, basket: 0.8,
};
// single objects drawn once, stretched to the shape instead of tiled
export const STRETCH = new Set(['tree', 'firepit', 'hottub', 'table']);

function rng(seed) {
  let a = seed >>> 0;
  return () => { a |= 0; a = (a + 0x6D2B79F5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}
const hash = (s) => [...s].reduce((h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) | 0, 7);
const rgb = (hex) => { const n = parseInt((hex || '#888888').slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; };
const tint = ([r, g, b], d, a = 1) => `rgba(${Math.max(0, Math.min(255, r + d))},${Math.max(0, Math.min(255, g + d))},${Math.max(0, Math.min(255, b + d))},${a})`;

// draw f at x and its wrapped copies so blobs crossing the edge tile seamlessly
function wrap(x, cx, cy, r, f) {
  for (const dx of [-SIZE, 0, SIZE]) for (const dy of [-SIZE, 0, SIZE]) {
    if (cx + dx + r < 0 || cx + dx - r > SIZE || cy + dy + r < 0 || cy + dy - r > SIZE) continue;
    f(cx + dx, cy + dy);
  }
}
function speckle(x, R, n, base, spread, size, alpha = 1) {
  for (let i = 0; i < n; i++) {
    x.fillStyle = tint(base, (R() - 0.5) * spread, alpha);
    x.fillRect(R() * SIZE, R() * SIZE, size * (0.5 + R()), size * (0.5 + R()));
  }
}
function slabs(x, R, cols, rows, base, joint, spread, grainN = 900) {
  x.fillStyle = joint; x.fillRect(0, 0, SIZE, SIZE);
  const w = SIZE / cols, h = SIZE / rows, j = 2.5;
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
    x.fillStyle = tint(base, (R() - 0.5) * spread);
    x.fillRect(c * w + j / 2, r * h + j / 2, w - j, h - j);
  }
  speckle(x, R, grainN, base, 40, 1.4, 0.35);
}
function bond(x, R, rows, cols, base, joint, spread, stagger = 0.5) {
  x.fillStyle = joint; x.fillRect(0, 0, SIZE, SIZE);
  const h = SIZE / rows, w = SIZE / cols, j = 2;
  for (let r = 0; r < rows; r++) {
    const off = (r % 2) * w * stagger;
    for (let c = -1; c <= cols; c++) {
      x.fillStyle = tint(base, (R() - 0.5) * spread);
      x.fillRect(c * w + off + j / 2, r * h + j / 2, w - j, h - j);
    }
  }
}
function boards(x, R, n, base, spread, grain = 'rgba(60,35,15,0.25)') {
  const h = SIZE / n;
  x.fillStyle = tint(base, -45); x.fillRect(0, 0, SIZE, SIZE);
  for (let i = 0; i < n; i++) {
    const cut = R() * SIZE;
    [[0, cut], [cut, SIZE]].forEach(([a, b]) => {
      x.fillStyle = tint(base, (R() - 0.5) * spread);
      x.fillRect(a + 1, i * h + 1.5, b - a - 2, h - 3);
    });
    x.strokeStyle = grain; x.lineWidth = 1;
    for (let g = 0; g < 7; g++) {
      const y = i * h + 3 + R() * (h - 6);
      x.beginPath(); x.moveTo(0, y); x.bezierCurveTo(SIZE * 0.3, y + R() * 3 - 1.5, SIZE * 0.6, y + R() * 3 - 1.5, SIZE, y); x.stroke();
    }
  }
}
function blob(x, cx, cy, r, base, R) {
  wrap(x, cx, cy, r, (px, py) => {
    const g = x.createRadialGradient(px - r * 0.35, py - r * 0.35, r * 0.1, px, py, r);
    g.addColorStop(0, tint(base, 55)); g.addColorStop(0.6, tint(base, 0)); g.addColorStop(1, tint(base, -45));
    x.fillStyle = 'rgba(0,0,0,0.28)'; x.beginPath(); x.arc(px + r * 0.18, py + r * 0.22, r, 0, 6.283); x.fill();
    x.fillStyle = g; x.beginPath();
    const lobes = 7 + Math.floor(R() * 4);
    for (let k = 0; k <= lobes; k++) {
      const a = (k / lobes) * 6.283, rr = r * (0.82 + R() * 0.22);
      if (k === 0) x.moveTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr); else x.lineTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr);
    }
    x.closePath(); x.fill();
  });
}

function voronoi(x, R, base, n, spread, joint, raised = false) {
  const pts = Array.from({ length: n }, () => [R() * SIZE, R() * SIZE, (R() - 0.5) * spread]);
  const all = []; pts.forEach(([px, py, t]) => { for (const dx of [-SIZE, 0, SIZE]) for (const dy of [-SIZE, 0, SIZE]) all.push([px + dx, py + dy, t]); });
  const img = x.createImageData(SIZE, SIZE), d = img.data;
  for (let yy = 0; yy < SIZE; yy++) for (let xx = 0; xx < SIZE; xx++) {
    let b1 = 1e12, b2 = 1e12, t1 = 0;
    for (let i = 0; i < all.length; i++) {
      const dx = all[i][0] - xx, dy = all[i][1] - yy, dd = dx * dx + dy * dy;
      if (dd < b1) { b2 = b1; b1 = dd; t1 = all[i][2]; } else if (dd < b2) b2 = dd;
    }
    const edge = Math.sqrt(b2) - Math.sqrt(b1), o = (yy * SIZE + xx) * 4;
    const w = raised ? Math.min(1, edge / 9) : (edge < 3.2 ? 0 : 1);
    const shade = raised ? (w * 0.6 + 0.4) : 1;
    const c = w > 0 ? base.map((v) => Math.max(0, Math.min(255, (v + t1) * shade))) : joint;
    d[o] = c[0]; d[o + 1] = c[1]; d[o + 2] = c[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  speckle(x, R, 1500, base, 40, 1.4, 0.3);
}

const PLANT_GREENS = ['#3f7d3a', '#5a9a3c', '#2f6b3a', '#7aa84a', '#4c8a55', '#8fae5a', '#6f9f6a'];
const FLOWERS = ['#f4f1ea', '#e7c1d6', '#b48ad1', '#f2d35b', '#e88b8b'];

const DRAW = {
  turf: (x, R) => {
    const base = [88, 150, 60];
    for (let s = 0; s < 2; s++) {
      const g = x.createLinearGradient(s * SIZE / 2, 0, (s + 1) * SIZE / 2, 0);
      const d = s ? 5 : -2; g.addColorStop(0, tint(base, d - 1)); g.addColorStop(0.5, tint(base, d + 2)); g.addColorStop(1, tint(base, d - 1));
      x.fillStyle = g; x.fillRect(s * SIZE / 2, 0, SIZE / 2, SIZE);
    }
    for (let i = 0; i < 9000; i++) {
      const px = R() * SIZE, py = R() * SIZE;
      x.strokeStyle = tint(base, (R() - 0.45) * 70, 0.55); x.lineWidth = 1;
      x.beginPath(); x.moveTo(px, py); x.lineTo(px + R() * 3 - 1.5, py - 2 - R() * 4); x.stroke();
    }
  },
  artificial: (x, R) => {
    x.fillStyle = '#4f9d3f'; x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 7000; i++) { x.fillStyle = tint([79, 157, 63], (R() - 0.5) * 40, 0.6); x.fillRect(R() * SIZE, R() * SIZE, 1.5, 3); }
  },
  sandstone: (x, R) => slabs(x, R, 4, 4, [214, 196, 162], '#a39579', 34),
  porcelain: (x, R) => slabs(x, R, 2, 3, [214, 210, 202], '#b4afa6', 14, 400),
  concslab: (x, R) => slabs(x, R, 4, 4, [200, 199, 192], '#9e9d97', 16),
  concrete: (x, R) => { x.fillStyle = '#cfcec8'; x.fillRect(0, 0, SIZE, SIZE); speckle(x, R, 6000, [207, 206, 200], 34, 1.6, 0.5); },
  block: (x, R, col) => bond(x, R, 12, 6, rgb(col), tint(rgb(col), -45), 34),
  resin: (x, R) => { x.fillStyle = '#b39a74'; x.fillRect(0, 0, SIZE, SIZE); speckle(x, R, 14000, [170, 146, 108], 90, 2.2); },
  tarmac: (x, R) => { x.fillStyle = '#34383b'; x.fillRect(0, 0, SIZE, SIZE); speckle(x, R, 9000, [55, 58, 61], 40, 1.6); },
  gravel: (x, R, col) => {
    const gb = rgb(col);
    x.fillStyle = tint(gb, -30); x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 2600; i++) {
      const cx = R() * SIZE, cy = R() * SIZE, r = 3 + R() * 5, c = gb.map((v) => v + (R() - 0.5) * 50);
      wrap(x, cx, cy, r, (px, py) => {
        x.fillStyle = 'rgba(60,50,40,0.35)'; x.beginPath(); x.ellipse(px + 1, py + 1.3, r, r * 0.8, 0, 0, 6.283); x.fill();
        x.fillStyle = tint(c, 0); x.beginPath(); x.ellipse(px, py, r, r * 0.8, R() * 3, 0, 6.283); x.fill();
      });
    }
  },
  decking: (x, R, col) => boards(x, R, 10, rgb(col), 26),
  timber: (x, R, col) => boards(x, R, 8, rgb(col), 22),
  sleeper: (x, R, col) => boards(x, R, 6, rgb(col), 28, 'rgba(40,25,10,0.35)'),
  brick: (x, R, col) => bond(x, R, 8, 4, rgb(col), '#cfc6b6', 40),
  concblock: (x, R) => bond(x, R, 8, 4, [184, 188, 192], '#8f9498', 14),
  render: (x, R) => { x.fillStyle = '#ece8df'; x.fillRect(0, 0, SIZE, SIZE); speckle(x, R, 5000, [236, 232, 223], 18, 1.4, 0.5); },
  slate: (x, R) => bond(x, R, 10, 5, [85, 88, 92], '#3c3f42', 20),
  soil: (x, R) => { x.fillStyle = '#4b3424'; x.fillRect(0, 0, SIZE, SIZE); speckle(x, R, 9000, [75, 52, 36], 40, 2.2); },
  planting: (x, R) => {
    DRAW.soil(x, R);
    for (let i = 0; i < 26; i++) {
      const r = 18 + R() * 34;
      blob(x, R() * SIZE, R() * SIZE, r, rgb(PLANT_GREENS[Math.floor(R() * PLANT_GREENS.length)]), R);
    }
    for (let i = 0; i < 9; i++) {
      const cx = R() * SIZE, cy = R() * SIZE, col = FLOWERS[Math.floor(R() * FLOWERS.length)];
      for (let k = 0; k < 14; k++) wrap(x, cx, cy, 20, (px, py) => { x.fillStyle = col; x.beginPath(); x.arc(px + (R() - 0.5) * 30, py + (R() - 0.5) * 30, 2.5 + R() * 2, 0, 6.283); x.fill(); });
    }
  },
  hedge: (x, R) => {
    x.fillStyle = '#2c5a2e'; x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 90; i++) blob(x, R() * SIZE, R() * SIZE, 20 + R() * 26, rgb(['#3d7a3e', '#356f37', '#4a8a46'][i % 3]), R);
  },
  water: (x, R) => {
    x.fillStyle = '#3577a8'; x.fillRect(0, 0, SIZE, SIZE);
    speckle(x, R, 3000, [53, 119, 168], 22, 3, 0.5);
    x.strokeStyle = 'rgba(255,255,255,0.22)'; x.lineWidth = 2;
    for (let i = 0; i < 40; i++) {
      const cy = R() * SIZE, cx = R() * SIZE;
      wrap(x, cx, cy, 45, (px, py) => { x.beginPath(); x.moveTo(px - 20, py); x.quadraticCurveTo(px, py - 6, px + 20, py); x.stroke(); });
    }
  },
  glass: (x) => {
    x.fillStyle = '#cfe3e6'; x.fillRect(0, 0, SIZE, SIZE);
    x.strokeStyle = '#7d8f92'; x.lineWidth = 5;
    for (let i = 0; i <= 4; i++) { x.beginPath(); x.moveTo(i * SIZE / 4, 0); x.lineTo(i * SIZE / 4, SIZE); x.stroke(); }
    x.fillStyle = 'rgba(255,255,255,0.35)'; x.fillRect(0, 0, SIZE, SIZE / 3);
  },
  cobble: (x, R) => slabs(x, R, 6, 6, [120, 124, 128], '#4f5357', 40, 600),
  ashlar: (x, R, col) => {
    // Indian-sandstone style mixed patio pack: 900/600/300 modules in rows of 600 & 300
    const base = rgb(col), u = SIZE / 6, rows = [2, 1, 2, 1];
    x.fillStyle = tint(base, -55); x.fillRect(0, 0, SIZE, SIZE);
    let y = 0;
    rows.forEach((rh) => {
      let xx = 0;
      while (xx < 6) {
        const opts = [3, 2, 1].filter((w) => w <= 6 - xx && (w >= rh || R() < 0.5));
        const w = opts.length ? opts[Math.floor(R() * opts.length)] : 6 - xx;
        const subRows = w < rh ? rh : 1;
        for (let k = 0; k < subRows; k++) {
          const hh = rh / subRows;
          x.fillStyle = tint(base, (R() - 0.5) * 38);
          x.fillRect(xx * u + 1.5, (y + k * hh) * u + 1.5, w * u - 3, hh * u - 3);
        }
        xx += w;
      }
      y += rh;
    });
    speckle(x, R, 1400, base, 46, 1.5, 0.3);
    for (let i = 0; i < 40; i++) { x.strokeStyle = tint(base, -25, 0.25); x.lineWidth = 1; const yy = R() * SIZE; x.beginPath(); x.moveTo(R() * SIZE, yy); x.lineTo(R() * SIZE, yy + (R() - 0.5) * 30); x.stroke(); }
  },
  setts: (x, R, col) => bond(x, R, 6, 4, rgb(col), '#3e4146', 46),
  basket: (x, R, col) => {
    const base = rgb(col), q = SIZE / 4, j = 2;
    x.fillStyle = '#cfc6b6'; x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 4; i++) for (let k = 0; k < 4; k++) {
      const horiz = (i + k) % 2 === 0;
      for (let b = 0; b < 2; b++) {
        x.fillStyle = tint(base, (R() - 0.5) * 40);
        if (horiz) x.fillRect(i * q + j / 2, k * q + b * q / 2 + j / 2, q - j, q / 2 - j);
        else x.fillRect(i * q + b * q / 2 + j / 2, k * q + j / 2, q / 2 - j, q - j);
      }
    }
  },
  crazy: (x, R, col) => voronoi(x, R, rgb(col), 34, 30, [70, 64, 56]),
  drystone: (x, R, col) => voronoi(x, R, rgb(col), 60, 40, [40, 38, 34], true),
  bark: (x, R) => {
    x.fillStyle = '#3e2618'; x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 5200; i++) {
      const cx = R() * SIZE, cy = R() * SIZE, l = 5 + R() * 10, a = R() * 3.14;
      x.strokeStyle = tint([112, 72, 42], (R() - 0.5) * 60); x.lineWidth = 2 + R() * 3; x.lineCap = 'round';
      wrap(x, cx, cy, l, (px, py) => { x.beginPath(); x.moveTo(px - Math.cos(a) * l / 2, py - Math.sin(a) * l / 2); x.lineTo(px + Math.cos(a) * l / 2, py + Math.sin(a) * l / 2); x.stroke(); });
    }
  },
  pebbles: (x, R, col) => {
    const base = rgb(col); x.fillStyle = tint(base, -60); x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 520; i++) {
      const cx = R() * SIZE, cy = R() * SIZE, r = 9 + R() * 12, c = tint(base, (R() - 0.5) * 70);
      wrap(x, cx, cy, r, (px, py) => {
        x.fillStyle = 'rgba(0,0,0,0.35)'; x.beginPath(); x.ellipse(px + 2, py + 3, r, r * 0.75, 0, 0, 6.283); x.fill();
        const g = x.createRadialGradient(px - r * 0.3, py - r * 0.3, 1, px, py, r); g.addColorStop(0, 'rgba(255,255,255,0.45)'); g.addColorStop(0.4, c); g.addColorStop(1, c);
        x.fillStyle = g; x.beginPath(); x.ellipse(px, py, r, r * 0.75, R() * 3, 0, 6.283); x.fill();
      });
    }
  },
  grasscrete: (x, R) => {
    DRAW.turf(x, R);
    const n = 6, c = SIZE / n;
    x.fillStyle = '#b9b7ae';
    for (let i = 0; i < n; i++) { x.fillRect(i * c, 0, c * 0.28, SIZE); x.fillRect(0, i * c, SIZE, c * 0.28); }
    speckle(x, R, 1500, [185, 183, 174], 30, 1.5, 0.5);
  },
  slatted: (x, R, col) => {
    const base = rgb(col), n = 14, h = SIZE / n;
    x.fillStyle = '#1d1f22'; x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < n; i++) { x.fillStyle = tint(base, (R() - 0.5) * 20); x.fillRect(0, i * h + 3, SIZE, h - 6); }
  },
  corten: (x, R, col) => {
    const b = rgb(col); x.fillStyle = tint(b, 0); x.fillRect(0, 0, SIZE, SIZE);
    for (let i = 0; i < 90; i++) {
      const cx = R() * SIZE, cy = R() * SIZE, r = 10 + R() * 30, c = tint([b[0] + 30, b[1] + 8, b[2] - 12], (R() - 0.5) * 30, 0.35);
      wrap(x, cx, cy, r, (px, py) => { x.fillStyle = c; x.beginPath(); x.arc(px, py, r, 0, 6.283); x.fill(); });
    }
    speckle(x, R, 9000, b, 80, 2.2, 0.7);
  },
  rooftile: (x, R, col) => {
    const base = rgb(col), rows = 10, h = SIZE / rows, cols = 8, w = SIZE / cols;
    x.fillStyle = tint(base, -45); x.fillRect(0, 0, SIZE, SIZE);
    for (let r = 0; r < rows; r++) for (let c = -1; c <= cols; c++) {
      const off = (r % 2) * w / 2;
      const g = x.createLinearGradient(0, r * h, 0, (r + 1) * h); const t0 = (R() - 0.5) * 22;
      g.addColorStop(0, tint(base, t0 + 14)); g.addColorStop(1, tint(base, t0 - 26));
      x.fillStyle = g; x.fillRect(c * w + off + 1, r * h, w - 2, h - 1.5);
    }
  },
  woodplank: (x, R, col) => {
    bond(x, R, 12, 2, rgb(col), '#8d8378', 26, 0.33);
    x.strokeStyle = 'rgba(70,45,25,0.18)'; x.lineWidth = 1;
    for (let i = 0; i < 160; i++) { const y = R() * SIZE; x.beginPath(); x.moveTo(0, y); x.bezierCurveTo(170, y + 2, 340, y - 2, SIZE, y); x.stroke(); }
  },
  veg: (x, R) => {
    DRAW.soil(x, R);
    const rows = 5, h = SIZE / rows;
    for (let r = 0; r < rows; r++) for (let i = 0; i < 9; i++) blob(x, (i + 0.5) * SIZE / 9 + (R() - 0.5) * 8, (r + 0.5) * h, 14 + R() * 8, rgb(PLANT_GREENS[(r * 3 + i) % PLANT_GREENS.length]), R);
  },
  pool: (x, R) => {
    x.fillStyle = '#3fb2c8'; x.fillRect(0, 0, SIZE, SIZE);
    x.strokeStyle = 'rgba(255,255,255,0.28)'; x.lineWidth = 2.5;
    for (let i = 0; i < 70; i++) {
      const cx = R() * SIZE, cy = R() * SIZE, r = 14 + R() * 24;
      wrap(x, cx, cy, r, (px, py) => { x.beginPath(); for (let k = 0; k <= 6; k++) { const a = k / 6 * 6.283, rr = r * (0.7 + R() * 0.5); if (k) x.lineTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr); else x.moveTo(px + rr, py); } x.stroke(); });
    }
  },
  tree: (x, R, col) => {
    const c = SIZE / 2, base = rgb(col);
    x.fillStyle = 'rgba(0,0,0,0.25)'; x.beginPath(); x.arc(c + 26, c + 30, c * 0.88, 0, 6.283); x.fill();
    for (let i = 0; i < 70; i++) {
      const a = R() * 6.283, d = Math.sqrt(R()) * c * 0.62;
      blob(x, c + Math.cos(a) * d, c + Math.sin(a) * d, c * (0.16 + R() * 0.16), base, R);
    }
  },
  firepit: (x, R) => {
    const c = SIZE / 2;
    for (let i = 0; i < 22; i++) {
      const a = (i / 22) * 6.283; x.fillStyle = tint([140, 136, 128], (R() - 0.5) * 50);
      x.beginPath(); x.ellipse(c + Math.cos(a) * c * 0.8, c + Math.sin(a) * c * 0.8, c * 0.17, c * 0.12, a, 0, 6.283); x.fill();
    }
    const g = x.createRadialGradient(c, c, 4, c, c, c * 0.65); g.addColorStop(0, '#f59e0b'); g.addColorStop(0.35, '#7c2d12'); g.addColorStop(1, '#1c1917');
    x.fillStyle = g; x.beginPath(); x.arc(c, c, c * 0.65, 0, 6.283); x.fill();
  },
  hottub: (x, R) => {
    boards(x, R, 12, [122, 84, 52], 20);
    x.fillStyle = '#5b6168'; x.beginPath(); x.roundRect(SIZE * 0.1, SIZE * 0.1, SIZE * 0.8, SIZE * 0.8, 40); x.fill();
    x.fillStyle = '#4fb5c9'; x.beginPath(); x.roundRect(SIZE * 0.16, SIZE * 0.16, SIZE * 0.68, SIZE * 0.68, 30); x.fill();
    x.strokeStyle = 'rgba(255,255,255,0.35)'; x.lineWidth = 3; for (let i = 0; i < 8; i++) { x.beginPath(); x.arc(SIZE / 2, SIZE / 2, 30 + i * 18, R() * 3, R() * 3 + 1.5); x.stroke(); }
  },
  table: (x) => {
    const c = SIZE / 2;
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * 6.283;
      x.save(); x.translate(c + Math.cos(a) * c * 0.74, c + Math.sin(a) * c * 0.74); x.rotate(a + Math.PI / 2);
      x.fillStyle = 'rgba(0,0,0,0.25)'; x.fillRect(-c * 0.15 + 5, -c * 0.13 + 6, c * 0.3, c * 0.26);
      x.fillStyle = '#5b6168'; x.fillRect(-c * 0.15, -c * 0.13, c * 0.3, c * 0.26); x.restore();
    }
    x.fillStyle = 'rgba(0,0,0,0.25)'; x.beginPath(); x.arc(c + 8, c + 10, c * 0.5, 0, 6.283); x.fill();
    x.fillStyle = '#e8e2d6'; x.beginPath(); x.arc(c, c, c * 0.5, 0, 6.283); x.fill();
    x.strokeStyle = '#b9b2a4'; x.lineWidth = 4; x.stroke();
  },
  kerb: (x, R) => slabs(x, R, 2, 8, [176, 174, 166], '#7d7b74', 18, 500),
};

// Returns { url, canvas, tileM } for a texture kind (colour-aware for timbers/brick)
export function getTexture(kind, color) {
  const k = DRAW[kind] ? kind : 'concrete';
  const key = `${k}|${color || ''}`;
  if (cache.has(key)) return cache.get(key);
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = SIZE;
  DRAW[k](canvas.getContext('2d'), rng(hash(key)), color || '#a9743b');
  const t = { canvas, url: canvas.toDataURL(STRETCH.has(k) ? 'image/png' : 'image/jpeg', 0.85), tileM: TILE_M[k] || 1, stretch: STRETCH.has(k) };
  cache.set(key, t);
  return t;
}

export const hasTexture = (kind) => !!DRAW[kind];

export const EDGES = [
  { id: 'none', label: 'No border' },
  { id: 'cobble', label: 'Cobble / sett border', m: 0.12 },
  { id: 'kerb', label: 'Kerb edging', m: 0.15 },
  { id: 'brick', label: 'Brick soldier course', m: 0.11, color: '#9c4a3a' },
];

export const GROUNDS = [
  { id: 'turf', label: 'Lawn' },
  { id: 'soil', label: 'Soil / site' },
  { id: 'plain', label: 'Plain grid' },
];

const LIGHT_INK = new Set(['planting', 'soil', 'water', 'hedge', 'tarmac', 'block', 'slate', 'bark', 'veg', 'tree', 'firepit', 'hottub', 'pool', 'slatted', 'corten', 'rooftile']);
// Label colour that reads on the texture actually drawn (falls back to the flat colour)
export const inkFor = (tex, hex, realistic) => ((realistic && LIGHT_INK.has(tex)) || isDark(hex) ? '#ffffff' : '#1f2937');

export const isDark = (hex) => { const [r, g, b] = rgb(hex); return 0.299 * r + 0.587 * g + 0.114 * b < 110; };
